import React, { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Plus, Package, AlertTriangle, Trash2, ArrowUpDown, Sparkles, FileDown, Upload } from 'lucide-react';

const categoryOptions = [
  { value: 'drinks_ingredient', label: 'Drinks Ingredient' },
  { value: 'food_ingredient', label: 'Food Ingredient' },
  { value: 'shisha_supply', label: 'Shisha Supply' },
  { value: 'other', label: 'Other' },
];
const roleOptions = [
  { value: 'bartender', label: 'Bartender' },
  { value: 'chef', label: 'Chef' },
  { value: 'shisha_expert', label: 'Shisha Expert' },
  { value: 'manager', label: 'Manager' },
];

export default function InventoryTab() {
  const [items, setItems] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [adjustDialog, setAdjustDialog] = useState(null);
  const [adjustQty, setAdjustQty] = useState('');
  const [adjustReason, setAdjustReason] = useState('');
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState({ name: '', category: 'drinks_ingredient', unit: 'ml', current_stock: 0, min_stock_level: 10, cost_per_unit: 0, owner_role: 'manager', supplier_id: '' });

  const fetchData = useCallback(async () => {
    try {
      const [itemsRes, alertsRes, supRes] = await Promise.all([
        api.get('/inventory'),
        api.get('/inventory/alerts'),
        api.get('/suppliers'),
      ]);
      setItems(itemsRes.data);
      setAlerts(alertsRes.data);
      setSuppliers(supRes.data);
    } catch { /* */ }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const resetForm = () => { setForm({ name: '', category: 'drinks_ingredient', unit: 'ml', current_stock: 0, min_stock_level: 10, cost_per_unit: 0, owner_role: 'manager', supplier_id: '' }); setEditItem(null); };
  const openNew = () => { resetForm(); setFormOpen(true); };
  const openEdit = (item) => { setForm({ ...item, current_stock: item.current_stock, min_stock_level: item.min_stock_level, cost_per_unit: item.cost_per_unit, supplier_id: item.supplier_id || '' }); setEditItem(item); setFormOpen(true); };

  const saveItem = async () => {
    const payload = { ...form, current_stock: parseFloat(form.current_stock), min_stock_level: parseFloat(form.min_stock_level), cost_per_unit: parseFloat(form.cost_per_unit) };
    try {
      if (editItem) { await api.put(`/inventory/${editItem.id}`, payload); toast.success('Updated'); }
      else { await api.post('/inventory', payload); toast.success('Created'); }
      setFormOpen(false); resetForm(); fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const deleteItem = async (id) => {
    try { await api.delete(`/inventory/${id}`); toast.success('Deleted'); fetchData(); } catch { toast.error('Failed'); }
  };

  const adjustStock = async () => {
    if (!adjustQty || !adjustReason) { toast.error('Quantity and reason required'); return; }
    try {
      await api.post(`/inventory/${adjustDialog}/adjust`, { quantity: parseFloat(adjustQty), reason: adjustReason, adjustment_type: 'manual' });
      toast.success('Stock adjusted');
      setAdjustDialog(null); setAdjustQty(''); setAdjustReason(''); fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const seedFromMenu = async () => {
    if (!window.confirm('Seed inventory from the current menu? This adds all missing shisha flavours, food ingredients, and drinks. Existing items are preserved.')) return;
    try {
      const { data } = await api.post('/inventory/seed-from-menu');
      toast.success(data.message || 'Seeded');
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Seed failed'); }
  };

  // Stock-take PDF + bulk update
  const [pdfCategory, setPdfCategory] = useState('all');
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkCsv, setBulkCsv] = useState('');
  const [bulkSubmitting, setBulkSubmitting] = useState(false);

  const downloadStockTakePdf = async () => {
    try {
      const res = await api.get(`/inventory/stock-take-pdf?category=${pdfCategory}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      const stamp = new Date().toISOString().slice(0, 10);
      a.download = `stock-take-${pdfCategory}-${stamp}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Stock-take PDF downloaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'PDF generation failed');
    }
  };

  const submitBulkUpdate = async () => {
    const lines = bulkCsv.split('\n').map(l => l.trim()).filter(Boolean);
    const rows = [];
    for (const line of lines) {
      // Accept "Name,Qty" or "Name,Qty,Notes"
      const parts = line.split(',').map(p => p.trim());
      if (parts.length < 2) continue;
      const qty = parseFloat(parts[parts.length >= 3 ? parts.length - 2 : 1]);
      if (Number.isNaN(qty)) continue;
      const notes = parts.length >= 3 ? parts.slice(-1)[0] : '';
      const name = parts.length >= 3 ? parts.slice(0, -2).join(',').trim() : parts[0];
      rows.push({ name, counted_qty: qty, notes });
    }
    if (rows.length === 0) { toast.error('No valid rows. Format: Name,CountedQty[,Notes]'); return; }
    setBulkSubmitting(true);
    try {
      const { data } = await api.post('/inventory/bulk-update-counts', { rows });
      toast.success(data.message || 'Updated');
      if (data.not_found?.length) toast.warning(`Not matched: ${data.not_found.join(', ')}`);
      setBulkOpen(false);
      setBulkCsv('');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bulk update failed');
    } finally {
      setBulkSubmitting(false);
    }
  };

  const catColors = { drinks_ingredient: 'bg-blue-accent/20 text-blue-accent', food_ingredient: 'bg-amber-500/20 text-amber-400', shisha_supply: 'bg-purple-500/20 text-purple-400', other: 'bg-zinc-500/20 text-zinc-400' };

  return (
    <div>
      {/* Alerts */}
      {alerts.length > 0 && (
        <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-sm">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="w-4 h-4 text-red-400" />
            <span className="text-red-400 font-sans text-xs uppercase tracking-wider font-bold" data-testid="stock-alerts-title">Stock Alerts ({alerts.length})</span>
          </div>
          <div className="space-y-1">
            {alerts.slice(0, 5).map(a => (
              <div key={a.id} className="flex items-center justify-between text-sm">
                <span className="text-zinc-300 font-sans">{a.name}</span>
                <div className="flex items-center gap-2">
                  <span className="text-zinc-500 font-mono text-xs">{a.current_stock} {a.unit}</span>
                  <Badge className={`${a.alert_level === 'critical' ? 'bg-red-500/20 text-red-400' : 'bg-amber-500/20 text-amber-400'} border-0 font-sans text-xs`}>{a.alert_level}</Badge>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex justify-between items-center mb-4 flex-wrap gap-2">
        <h3 className="text-sm font-sans text-zinc-400 uppercase tracking-wider">{items.length} items</h3>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-1 bg-white/[0.03] border border-white/10 rounded-sm pl-2">
            <span className="text-[10px] uppercase tracking-wider text-zinc-500 font-sans">PDF</span>
            <Select value={pdfCategory} onValueChange={setPdfCategory}>
              <SelectTrigger className="w-[130px] bg-transparent border-0 text-white font-sans text-xs h-8" data-testid="stock-take-category-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-bg-secondary border border-white/10">
                <SelectItem value="all">All Categories</SelectItem>
                <SelectItem value="shisha_supply">Shisha Only</SelectItem>
                <SelectItem value="food_ingredient">Food Only</SelectItem>
                <SelectItem value="drinks_ingredient">Drinks Only</SelectItem>
              </SelectContent>
            </Select>
            <Button size="sm" variant="ghost" onClick={downloadStockTakePdf} className="h-8 text-gold hover:bg-gold/10 font-sans text-xs font-bold px-2" data-testid="download-stock-take-pdf-button">
              <FileDown className="w-3 h-3 mr-1" /> Download
            </Button>
          </div>
          <Button size="sm" variant="outline" onClick={() => setBulkOpen(true)} className="border-white/20 text-white hover:bg-white/5 rounded-sm font-sans text-xs font-bold" data-testid="bulk-update-inventory-button">
            <Upload className="w-3 h-3 mr-1" /> Bulk Update
          </Button>
          <Button size="sm" variant="outline" onClick={seedFromMenu} className="border-gold/40 text-gold hover:bg-gold/10 rounded-sm font-sans text-xs font-bold" data-testid="seed-inventory-from-menu-button"><Sparkles className="w-3 h-3 mr-1" /> Seed from Menu</Button>
          <Dialog open={formOpen} onOpenChange={setFormOpen}>
            <DialogTrigger asChild>
              <Button size="sm" onClick={openNew} className="bg-gold text-black rounded-sm font-sans text-xs font-bold" data-testid="add-inventory-item-button"><Plus className="w-3 h-3 mr-1" /> Add Item</Button>
            </DialogTrigger>
          <DialogContent className="bg-bg-secondary border border-white/10 max-w-md">
            <DialogHeader><DialogTitle className="font-serif text-white">{editItem ? 'Edit' : 'New'} Inventory Item</DialogTitle></DialogHeader>
            <div className="space-y-3 pt-2 max-h-[60vh] overflow-y-auto">
              <div className="space-y-1">
                <Label className="text-xs uppercase tracking-[0.15em] text-zinc-400 font-sans">Name</Label>
                <Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} className="bg-transparent border border-white/10 text-white font-sans" data-testid="inventory-name-input" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1">
                  <Label className="text-xs uppercase tracking-[0.15em] text-zinc-400 font-sans">Category</Label>
                  <Select value={form.category} onValueChange={v => setForm(p => ({ ...p, category: v }))}>
                    <SelectTrigger className="bg-transparent border border-white/10 text-white font-sans" data-testid="inventory-category-select"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-bg-secondary border border-white/10">{categoryOptions.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs uppercase tracking-[0.15em] text-zinc-400 font-sans">Unit</Label>
                  <Select value={form.unit} onValueChange={v => setForm(p => ({ ...p, unit: v }))}>
                    <SelectTrigger className="bg-transparent border border-white/10 text-white font-sans"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-bg-secondary border border-white/10">
                      {['ml', 'l', 'g', 'kg', 'pieces'].map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div className="space-y-1">
                  <Label className="text-xs uppercase tracking-[0.15em] text-zinc-400 font-sans">Stock</Label>
                  <Input type="number" value={form.current_stock} onChange={e => setForm(p => ({ ...p, current_stock: e.target.value }))} className="bg-transparent border border-white/10 text-white font-sans" data-testid="inventory-stock-input" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs uppercase tracking-[0.15em] text-zinc-400 font-sans">Min Level</Label>
                  <Input type="number" value={form.min_stock_level} onChange={e => setForm(p => ({ ...p, min_stock_level: e.target.value }))} className="bg-transparent border border-white/10 text-white font-sans" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs uppercase tracking-[0.15em] text-zinc-400 font-sans">Cost/Unit</Label>
                  <Input type="number" step="0.01" value={form.cost_per_unit} onChange={e => setForm(p => ({ ...p, cost_per_unit: e.target.value }))} className="bg-transparent border border-white/10 text-white font-sans" />
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-xs uppercase tracking-[0.15em] text-zinc-400 font-sans">Owner Role</Label>
                <Select value={form.owner_role} onValueChange={v => setForm(p => ({ ...p, owner_role: v }))}>
                  <SelectTrigger className="bg-transparent border border-white/10 text-white font-sans"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-bg-secondary border border-white/10">{roleOptions.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs uppercase tracking-[0.15em] text-zinc-400 font-sans">Supplier (optional)</Label>
                <Select value={form.supplier_id || 'none'} onValueChange={v => setForm(p => ({ ...p, supplier_id: v === 'none' ? '' : v }))}>
                  <SelectTrigger className="bg-transparent border border-white/10 text-white font-sans" data-testid="inventory-supplier-select">
                    <SelectValue placeholder="No supplier assigned" />
                  </SelectTrigger>
                  <SelectContent className="bg-bg-secondary border border-white/10">
                    <SelectItem value="none">No supplier</SelectItem>
                    {suppliers.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                {suppliers.length === 0 && (
                  <p className="text-[11px] text-zinc-600 font-sans pt-1">Add suppliers in the Suppliers tab to enable assignment.</p>
                )}
              </div>
              <Button onClick={saveItem} className="w-full bg-gold text-black font-bold rounded-sm font-sans" data-testid="save-inventory-item-button">{editItem ? 'Update' : 'Create'}</Button>
            </div>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Items List */}
      <div className="space-y-2">
        {items.map(item => {
          const isLow = item.current_stock <= item.min_stock_level;
          const isCritical = item.current_stock <= item.min_stock_level * 0.5;
          return (
            <div key={item.id} className={`flex items-center justify-between p-3 bg-white/[0.03] border rounded-sm ${isCritical ? 'border-red-500/30' : isLow ? 'border-amber-500/20' : 'border-white/5'}`} data-testid={`inventory-item-${item.id}`}>
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <Package className={`w-4 h-4 flex-shrink-0 ${isCritical ? 'text-red-400' : isLow ? 'text-amber-400' : 'text-zinc-500'}`} />
                <div className="min-w-0">
                  <p className="text-white font-sans text-sm truncate">{item.name}</p>
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge className={`${catColors[item.category] || catColors.other} border-0 font-sans text-xs`}>{item.category.replace('_', ' ')}</Badge>
                    <span className="text-zinc-600 text-xs font-sans">{item.owner_role}</span>
                    {item.supplier_id ? (
                      <span className="text-blue-accent/80 text-xs font-sans">→ {suppliers.find(s => s.id === item.supplier_id)?.name || 'Unknown supplier'}</span>
                    ) : (
                      <span className="text-zinc-700 text-xs font-sans italic">no supplier</span>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="text-right">
                  <p className={`font-mono text-sm ${isCritical ? 'text-red-400' : isLow ? 'text-amber-400' : 'text-white'}`}>{item.current_stock} {item.unit}</p>
                  <p className="text-zinc-600 text-xs font-mono">min: {item.min_stock_level}</p>
                </div>
                <Dialog open={adjustDialog === item.id} onOpenChange={open => { if (!open) setAdjustDialog(null); }}>
                  <DialogTrigger asChild>
                    <Button size="sm" variant="ghost" onClick={() => setAdjustDialog(item.id)} className="h-7 text-zinc-400 hover:text-white" data-testid={`adjust-stock-${item.id}`}><ArrowUpDown className="w-3 h-3" /></Button>
                  </DialogTrigger>
                  <DialogContent className="bg-bg-secondary border border-white/10 max-w-sm">
                    <DialogHeader><DialogTitle className="font-serif text-white">Adjust: {item.name}</DialogTitle></DialogHeader>
                    <div className="space-y-3 pt-2">
                      <p className="text-zinc-400 font-sans text-sm">Current: {item.current_stock} {item.unit}</p>
                      <div className="space-y-1">
                        <Label className="text-xs uppercase tracking-[0.15em] text-zinc-400 font-sans">Quantity (+/-)</Label>
                        <Input type="number" value={adjustQty} onChange={e => setAdjustQty(e.target.value)} className="bg-transparent border border-white/10 text-white font-sans" placeholder="+10 or -5" data-testid="adjust-quantity-input" />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs uppercase tracking-[0.15em] text-zinc-400 font-sans">Reason</Label>
                        <Input value={adjustReason} onChange={e => setAdjustReason(e.target.value)} className="bg-transparent border border-white/10 text-white font-sans" placeholder="Delivery received" data-testid="adjust-reason-input" />
                      </div>
                      <Button onClick={adjustStock} className="w-full bg-gold text-black font-bold rounded-sm font-sans" data-testid="confirm-adjust-button">Confirm Adjustment</Button>
                    </div>
                  </DialogContent>
                </Dialog>
                <Button size="sm" variant="ghost" onClick={() => openEdit(item)} className="h-7 text-zinc-400 hover:text-white font-sans text-xs">Edit</Button>
                <Button size="sm" variant="ghost" onClick={() => deleteItem(item.id)} className="h-7 text-red-400 hover:text-red-300"><Trash2 className="w-3 h-3" /></Button>
              </div>
            </div>
          );
        })}
        {items.length === 0 && <p className="text-zinc-500 font-sans text-center py-8" data-testid="no-inventory">No inventory items yet. Add your first item above.</p>}
      </div>

      {/* Bulk Update from CSV (stock-take results) */}
      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent className="bg-bg-secondary border border-white/10 max-w-xl">
          <DialogHeader>
            <DialogTitle className="font-serif text-white">Bulk Update Stock Counts</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <p className="text-xs text-zinc-400 font-sans leading-relaxed">
              Paste one item per line. Format: <span className="text-gold font-mono">Name,CountedQty</span>
              {' '}or <span className="text-gold font-mono">Name,CountedQty,Notes</span>.
              Each update creates a stock-take adjustment log entry.
            </p>
            <div className="space-y-1">
              <Label className="text-xs uppercase tracking-[0.15em] text-zinc-400 font-sans">
                Counts (from your printed stock-take sheet)
              </Label>
              <textarea
                value={bulkCsv}
                onChange={e => setBulkCsv(e.target.value)}
                rows={10}
                className="w-full bg-transparent border border-white/10 text-white font-mono text-xs p-3 rounded-sm resize-y"
                placeholder={`Paneer,7.5\nChicken Thigh (boneless),12.3,arrived today\nAl Fakher Mint,850`}
                data-testid="bulk-update-textarea"
              />
            </div>
            <div className="flex items-center gap-2 pt-1">
              <Button
                onClick={submitBulkUpdate}
                disabled={bulkSubmitting}
                className="flex-1 bg-gold text-black font-bold rounded-sm font-sans"
                data-testid="bulk-update-submit-button"
              >
                {bulkSubmitting ? 'Updating…' : 'Apply Updates'}
              </Button>
              <Button
                variant="outline"
                onClick={() => { setBulkCsv(''); setBulkOpen(false); }}
                className="border-white/20 text-white hover:bg-white/5 font-sans"
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
